import { Button, Dialog, Grid, Stack, Typography } from "@mui/material";
import React from "react";
import api from "src/store/features/api";
import Preloader from "src/utils/Preloader";
import { useDialogStyles } from "../UI/AreYouTalkingWithABrokerDialog";
import ReferLegalDialog from "../legal/ReferLegalDialog";
import { IReferralData, REFERRAL_DATA_DEFAULT } from "./defaults";
import useDeviceSizes from "./hooks/useDeviceSizes";
import DesktopReferral from "./referral/DesktopReferral";
import MobileReferral from "./referral/MobileReferral";
import OptInReferral from "./referral/OptInReferral";
import { trackEvent } from "src/Amplitude";
import { handleToFixedAmount } from "./helpers";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";

const Referrals = () => {
  const isUkUser = useIsUkUserCountry();

  // get device sizes
  const { isDesktop, isTablet, isMobile } = useDeviceSizes();

  // call api here
  // get customers referral data - value earned, percent earn, referral link
  const { data: referralData, isLoading: referralIsLoading } = api.useGetReferralsQuery(undefined, {
    skip: isUkUser,
  });

  const [openReferLegal, setOpenReferLegal] = React.useState<boolean>(false);
  const handleOpenReferLegal = () => {
    setOpenReferLegal(true);
  };
  const handleCloseReferLegal = () => {
    setOpenReferLegal(false);
  };

  const [fetchUrlError, setFetchUrlError] = React.useState<boolean>(false);
  const handleErrorModelClose = () => setFetchUrlError(false);

  const [skip, setSkip] = React.useState<boolean>(true);
  const {
    data: optIn,
    isLoading,
    isError,
    error,
  } = api.useCreateReferralUrlQuery(undefined, { skip: isUkUser || skip });
  const handleOptIn = () => {
    setSkip(false);
    trackEvent("Clicked accept to opt in", { page: "Portfolio" }, "RaF");
  };

  React.useEffect(() => {
    if (isError && error) {
      setFetchUrlError(true);
    }
  }, [isError, error]);

  const data = React.useMemo((): IReferralData => {
    if (!referralData) return REFERRAL_DATA_DEFAULT;
    let value: string | number = referralData.rewardsTotal.reduce((a, c) => a + c.value, 0);

    value = `${handleToFixedAmount(value)} USDC`;
    return {
      value: value,
      percent: referralData.referralProgram?.rewardPercent ?? "2%",
      code: referralData.referralCode ?? optIn?.referralCode ?? "",
    };
  }, [referralData, optIn]);

  const isOptedIn = (optIn?.referralOptIn || referralData?.referralOptIn) ?? false;

  if (isUkUser) {
    return null;
  }

  if (isLoading) return <Preloader />;
  return (
    <>
      {!isOptedIn ? (
        <OptInReferral handleOptIn={handleOpenReferLegal} isLoading={referralIsLoading} />
      ) : (
        <>
          {isDesktop && <DesktopReferral data={data} isLoading={referralIsLoading} />}
          {(isTablet || isMobile) && <MobileReferral data={data} isLoading={referralIsLoading} />}
        </>
      )}
      <ReferLegalDialog
        refetchUserQuery={handleOptIn}
        optIn={openReferLegal}
        setOptIn={setOpenReferLegal}
        setFetchUrlError={setFetchUrlError}
        onClose={handleCloseReferLegal}
      />
      <ErrorDialog open={fetchUrlError} onClose={handleErrorModelClose} />
    </>
  );
};

export default Referrals;

interface ErrorDialogProps {
  open: boolean;
  onClose: () => void;
}

const ErrorDialog = (props: ErrorDialogProps) => {
  const { open, onClose } = props;
  const classes = useDialogStyles();
  const errorMsg = "Failed to create referral account. Please contact support";

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paperProps }}>
      <Stack spacing={2}>
        <Grid item container justifyContent="center">
          <Typography align="center" sx={{ fontSize: "14px" }}>
            {errorMsg}
          </Typography>
        </Grid>
        <Grid container justifyContent="center" alignItems="center">
          <Button disableRipple onClick={onClose} className={classes.submitButton}>
            Close
          </Button>
        </Grid>
      </Stack>
    </Dialog>
  );
};
